import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Slider,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  Info as InfoIcon,
  FlashOn as FlashIcon,
  DirectionsCar as CarIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { payoutsService } from '@/services/payoutsService';
import { logger } from '@/utils/logger';

const log = logger.module('PayoutSettingsPage');

// Saved values only — the API has no defaults, so unset fields stay empty until an admin saves them
interface PayoutSettings {
  release_delay_hours?: number;
  minimum_payout?: number;
  instant_payout_enabled?: boolean;
  instant_payout_fee_percentage?: number | null;
}

const PayoutSettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [driverSettings, setDriverSettings] = useState<PayoutSettings>({});
  const [hostSettings, setHostSettings] = useState<PayoutSettings>({});
  // Whether each set of settings has been saved before (payouts are blocked until then)
  const [driverConfigured, setDriverConfigured] = useState(false);
  // What is saved, to tell whether the form has unsaved changes
  const [savedDriver, setSavedDriver] = useState<PayoutSettings>({});
  const [savedHost, setSavedHost] = useState<PayoutSettings>({});
  const [hostConfigured, setHostConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingDriver, setSavingDriver] = useState(false);
  const [savingHost, setSavingHost] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      log.info('Loading payout settings for drivers and hosts');

      // Load driver settings
      const driverResponse = await payoutsService.getPayoutSettings();
      log.debug('Driver payout settings received', { driverResponse });
      setDriverConfigured(!!(driverResponse.success && driverResponse.data));
      if (driverResponse.success && driverResponse.data) {
        setDriverSettings(driverResponse.data);
        setSavedDriver(driverResponse.data);
        log.info('Driver payout settings loaded successfully');
      }

      // Load host settings
      const hostResponse = await payoutsService.getHostPayoutSettings();
      log.debug('Host payout settings received', { hostResponse });
      setHostConfigured(!!(hostResponse.success && hostResponse.data));
      if (hostResponse.success && hostResponse.data) {
        setHostSettings(hostResponse.data);
        setSavedHost(hostResponse.data);
        log.info('Host payout settings loaded successfully');
      }
    } catch (error: any) {
      log.error('Error loading payout settings', { error, status: error.response?.status });
      if (error.response?.status !== 404) {
        toast.error('Failed to load payout settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDriver = async () => {
    // Validation
    const driverDelay = driverSettings.release_delay_hours;
    if (driverDelay === undefined || driverDelay < 0 || driverDelay > 72) {
      toast.error('Release delay must be between 0 and 72 hours');
      return;
    }
    const driverMinimum = driverSettings.minimum_payout;
    if (driverMinimum === undefined || !Number.isFinite(driverMinimum) || driverMinimum < 0) {
      toast.error('Enter a minimum payout of ₦0 or more');
      return;
    }
    const driverFee = driverSettings.instant_payout_fee_percentage;
    if (driverFee !== undefined && driverFee !== null && (driverFee < 0 || driverFee > 10)) {
      toast.error('Instant payout fee must be between 0% and 10%');
      return;
    }

    setSavingDriver(true);
    try {
      log.info('Saving driver payout settings', { driverSettings });
      const result = await payoutsService.updatePayoutSettings(driverSettings);
      log.info('Driver payout settings saved successfully', { result });
      toast.success('Driver payout settings saved successfully');
      await loadSettings();
    } catch (error: any) {
      log.error('Error saving driver payout settings', {
        error,
        status: error.response?.status,
        message: error.response?.data?.message,
        data: error.response?.data
      });
      toast.error(error.response?.data?.message || 'Failed to save driver payout settings');
    } finally {
      setSavingDriver(false);
    }
  };

  const handleSaveHost = async () => {
    // Validation
    const hostDelay = hostSettings.release_delay_hours;
    if (hostDelay === undefined || hostDelay < 0 || hostDelay > 168) {
      toast.error('Release delay must be between 0 and 168 hours (7 days)');
      return;
    }
    const hostMinimum = hostSettings.minimum_payout;
    if (hostMinimum === undefined || !Number.isFinite(hostMinimum) || hostMinimum < 0) {
      toast.error('Enter a minimum payout of ₦0 or more');
      return;
    }
    const hostFee = hostSettings.instant_payout_fee_percentage;
    if (hostFee !== undefined && hostFee !== null && (hostFee < 0 || hostFee > 10)) {
      toast.error('Instant payout fee must be between 0% and 10%');
      return;
    }

    setSavingHost(true);
    try {
      log.info('Saving host payout settings', { hostSettings });
      const result = await payoutsService.updateHostPayoutSettings(hostSettings);
      log.info('Host payout settings saved successfully', { result });
      toast.success('Host payout settings saved successfully');
      await loadSettings();
    } catch (error: any) {
      log.error('Error saving host payout settings', {
        error,
        status: error.response?.status,
        message: error.response?.data?.message,
        data: error.response?.data
      });
      toast.error(error.response?.data?.message || 'Failed to save host payout settings');
    } finally {
      setSavingHost(false);
    }
  };

  // Discard unsaved edits by reloading what's saved
  const handleDiscardChanges = async () => {
    await loadSettings();
    toast.success('Changes discarded');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentSettings = activeTab === 0 ? driverSettings : hostSettings;
  const setCurrentSettings = activeTab === 0 ? setDriverSettings : setHostSettings;
  const handleSave = activeTab === 0 ? handleSaveDriver : handleSaveHost;
  const configured = activeTab === 0 ? driverConfigured : hostConfigured;
  const savedSettings = activeTab === 0 ? savedDriver : savedHost;
  const SETTING_FIELDS: (keyof PayoutSettings)[] = ['release_delay_hours', 'minimum_payout', 'instant_payout_enabled', 'instant_payout_fee_percentage'];
  const hasChanges = SETTING_FIELDS.some(field => (currentSettings[field] ?? null) !== (savedSettings[field] ?? null));
  const saving = activeTab === 0 ? savingDriver : savingHost;
  const userType = activeTab === 0 ? 'drivers' : 'hosts';
  const maxDelayHours = activeTab === 0 ? 72 : 168;

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon />
          Payout Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure earnings release delay and minimum withdrawal amounts for drivers and hosts
        </Typography>
      </Box>

      {/* Tabs for Driver/Host Settings */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab icon={<CarIcon />} label="Driver Payouts" iconPosition="start" />
          <Tab icon={<HomeIcon />} label="Host Payouts" iconPosition="start" />
        </Tabs>
      </Box>

      {!configured && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Payout settings for {userType} haven't been saved yet. {userType.charAt(0).toUpperCase() + userType.slice(1)} can't
          request payouts until you set the minimum payout and release delay below and save.
        </Alert>
      )}

      {/* Earnings Release Delay Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimeIcon />
            Earnings Release Delay
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set how long after booking completion earnings become available for withdrawal
          </Typography>

          <Box sx={{ px: 2, mb: 2 }}>
            <Slider
              value={currentSettings.release_delay_hours ?? 0}
              onChange={(_, value) => setCurrentSettings({ ...currentSettings, release_delay_hours: value as number })}
              min={0}
              max={maxDelayHours}
              step={1}
              marks={activeTab === 0 ? [
                { value: 0, label: '0 hrs' },
                { value: 24, label: '24 hrs' },
                { value: 48, label: '48 hrs' },
                { value: 72, label: '72 hrs' },
              ] : [
                { value: 0, label: '0 hrs' },
                { value: 48, label: '48 hrs' },
                { value: 96, label: '96 hrs' },
                { value: 168, label: '168 hrs' },
              ]}
              valueLabelDisplay="on"
              valueLabelFormat={(value) => (currentSettings.release_delay_hours === undefined ? 'Not set' : `${value} hours`)}
              sx={{
                '& .MuiSlider-markLabel': {
                  fontSize: '0.75rem',
                },
              }}
            />
          </Box>

          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Protection Period:</strong> {userType.charAt(0).toUpperCase() + userType.slice(1)} can withdraw earnings {currentSettings.release_delay_hours ?? '—'} hours after booking completion.
              This delay protects against disputes, cancellations, and fraudulent activities.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Recommended:</strong> {activeTab === 0 ? '24-48 hours' : '48-72 hours'} provides a good balance between user satisfaction and platform protection.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Minimum Payout Amount Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Minimum Withdrawal Amount
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set the minimum balance {userType} must have to request a payout
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                type="number"
                label="Minimum Payout"
                value={currentSettings.minimum_payout ?? ''}
                onChange={(e) => setCurrentSettings({
                  ...currentSettings,
                  minimum_payout: e.target.value === '' ? undefined : Number(e.target.value)
                })}
                inputProps={{ min: 0, step: 500 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                }}
                helperText={`${userType.charAt(0).toUpperCase() + userType.slice(1)} must have at least this amount to request a payout`}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Setting
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {currentSettings.minimum_payout === undefined ? 'Not set' : `₦${currentSettings.minimum_payout.toLocaleString()}`}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              Setting this too low may increase transaction fees. Setting it too high may frustrate {userType} with low earnings.
              <strong> Recommended: {activeTab === 0 ? '₦5,000 - ₦10,000' : '₦10,000 - ₦20,000'}</strong>
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Instant Payout Feature Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FlashIcon color="warning" />
              <Typography variant="h6">
                Instant Payout (Beta)
              </Typography>
              <Chip label="Coming Soon" size="small" color="info" />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Allow {userType} to bypass the release delay by paying a fee for instant access to their earnings
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={currentSettings.instant_payout_enabled === true}
                onChange={(e) => setCurrentSettings({ ...currentSettings, instant_payout_enabled: e.target.checked })}
                disabled
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Enable Instant Payouts
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Currently disabled - Feature under development
                </Typography>
              </Box>
            }
          />

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                type="number"
                label="Instant Payout Fee"
                value={currentSettings.instant_payout_fee_percentage ?? ''}
                onChange={(e) => setCurrentSettings({
                  ...currentSettings,
                  instant_payout_fee_percentage: e.target.value === '' ? null : Number(e.target.value)
                })}
                inputProps={{ min: 0, max: 10, step: 0.5 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                helperText={`Fee charged to ${userType} for instant payout access`}
                fullWidth
                disabled
              />
            </Grid>
          </Grid>

          <Alert severity="info" icon={<FlashIcon />} sx={{ mt: 3, opacity: 0.7 }}>
            <Typography variant="body2">
              <strong>How it works:</strong> {userType.charAt(0).toUpperCase() + userType.slice(1)} can choose to pay a {currentSettings.instant_payout_fee_percentage ?? '—'}% fee to receive their earnings immediately,
              bypassing the {currentSettings.release_delay_hours ?? '—'}-hour waiting period. This feature will be enabled in a future update.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleDiscardChanges}
          disabled={saving || !hasChanges}
          startIcon={<ResetIcon />}
        >
          Discard Changes
        </Button>
      </Box>
    </Box>
  );
};

export default PayoutSettingsPage;
